import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Grid,
  TextField,
  Rating,
  Chip,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
} from "@mui/material";
import {
  PlayArrow,
  Save,
  Send,
  CheckCircle,
  ExpandMore,
  Visibility,
  DateRange,
  AccessTime,
  Assignment,
  Add,
  Delete,
  Edit,
  Star,
  StarBorder,
} from "@mui/icons-material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
const api = axios.create({ baseURL: API_URL });

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const EmployeeSelfAppraisal = () => {
  const [loading, setLoading] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [selfAppraisal, setSelfAppraisal] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [workSummaries, setWorkSummaries] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
   const [employeeId, setEmployeeId] = useState(null);
    const [error, setError] = useState('');
  // Form states
  const [overallSummary, setOverallSummary] = useState("");
  const [newWorkSummary, setNewWorkSummary] = useState({
    project_name: "",
    start_date: null,
    end_date: null,
    manager_name: "",
    work_description: "",
     key_highlights: "",
  challenges_faced: "",
  actions_taken: ""
  });

  const steps = ['Check Eligibility', 'Rate Goals', 'Work Summaries', 'Overall Summary'];

  // Get company_id from token
  const getCompanyId = () => {
    const token = localStorage.getItem("token");
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.company_id;
    } catch (error) {
      //console.error("Error parsing token:", error);
      return null;
    }
  };

  // Get employee_id from token
  // const getEmployeeid = () => {
  //   const token = localStorage.getItem("token");
  //   if (!token) return null;
  //   try {
  //     const payload = JSON.parse(atob(token.split(".")[1]));
  //     return payload.employee_id;
  //   } catch (error) {
  //     //console.error("Error parsing token:", error);
  //     return null;
  //   }
  // };
    const getEmployeeid = async () => {
    try {
       const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/user/current?company_id=${company_id}`, {
        withCredentials: true
      });

      if (!response.data || !response.data.employee_id) {
        throw new Error('Employee ID not found in user data');
      }

      setEmployeeId(response.data.employee_id);
      
      return response.data.employee_id;
    } catch (err) {
      //console.error("Error fetching current user:", err);
      setError('Failed to fetch user information');
      return null;
    }
  };

  // Check eligibility on component mount
  useEffect(() => {
    checkEligibility();
  }, []);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const checkEligibility = async () => {
    try {
      const token = localStorage.getItem('token');
      setLoading(true);
      const company_id = getCompanyId();
      const employee_id = await getEmployeeid();
      if (!company_id) {
        showSnackbar("Please login again", "error");
        return;
      }

      const response = await api.get(`/api/self-appraisal/check-eligibility?employee_id=${employee_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            
          },
          withCredentials:true
        }
      );
      setEligibility(response.data);
      
      if (response.data.eligible && response.data.existingAppraisal) {
        // Load existing appraisal
        loadSelfAppraisal(response.data.existingAppraisal.id);
      } else if (response.data.eligible) {
        setActiveStep(0);
      }
    } catch (error) {
      //console.error("Error checking eligibility:", error);
      showSnackbar(error.response?.data?.message || "Error checking eligibility", "error");
    } finally {
      setLoading(false);
    }
  };

  const startNewSelfAppraisal = async () => {
    try {
      const token = localStorage.getItem('token');
      setLoading(true);
      const company_id = getCompanyId();
      const employee_id =  await getEmployeeid();
      
      if (!company_id || !employee_id) {
        showSnackbar("Authentication error", "error");
        return;
      }

      const response = await api.post(`/api/self-appraisal/start?employee_id=${employee_id}`, {
        cycle_id: eligibility.cycle.id,
      },{
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          withCredentials:true
        
      });

      setSelfAppraisal(response.data.selfAppraisal);
      setRatings(response.data.ratings);
      setWorkSummaries(response.data.selfAppraisal.work_summaries || []);
      setOverallSummary(response.data.selfAppraisal.overall_summary || "");
      
      // Determine which step to start on
      if (response.data.hasGoals) {
        setActiveStep(1); // Start with Goals
      } else {
        setActiveStep(2); // Skip to Work Summaries
      }
      
      showSnackbar("Self-appraisal started successfully", "success");
    } catch (error) {
      //console.error("Error starting self-appraisal:", error);
      showSnackbar(error.response?.data?.message || "Error starting self-appraisal", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadSelfAppraisal = async (appraisalId) => {
    try {
      const token = localStorage.getItem('token');
      setLoading(true);
      const company_id = getCompanyId();
      const employee_id = await getEmployeeid();
      const response = await api.get(`/api/self-appraisal/${appraisalId}?employee_id=${employee_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          
          },
          withCredentials:true
        }
      );
      
      setSelfAppraisal(response.data.selfAppraisal);
      setRatings(response.data.ratings);
      setWorkSummaries(response.data.selfAppraisal.work_summaries || []);
      setOverallSummary(response.data.selfAppraisal.overall_summary || "");
      
      // Set appropriate step based on status and content
      if (response.data.selfAppraisal.status === 'Draft') {
        if (response.data.selfAppraisal.has_goals && response.data.ratings.length > 0) {
          setActiveStep(1); // Goals step
        } else {
          setActiveStep(2); // Work summaries step
        }
      } else if (response.data.selfAppraisal.status === 'Submitted') {
        setActiveStep(3); // Already submitted
      }
      
    } catch (error) {
      //console.error("Error loading self-appraisal:", error);
      showSnackbar("Error loading self-appraisal", "error");
    } finally {
      setLoading(false);
    }
  };

  // Update goal rating
  const updateRating = async (ratingId, updates) => {
    try {
      const token = localStorage.getItem('token');
      const company_id = getCompanyId();
      const employee_id = await getEmployeeid();
      const response = await api.put(
        `/api/self-appraisal/${selfAppraisal.id}/ratings/${ratingId}?employee_id=${employee_id}`,
        updates,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          withCredentials:true
        }
      );
      
      // Update local state
      setRatings(prev => prev.map(rating => 
        rating.id === ratingId ? response.data.rating : rating
      ));
      
      showSnackbar("Rating updated", "success");
      return true;
    } catch (error) {
      //console.error("Error updating rating:", error);
      showSnackbar("Error updating rating", "error");
      return false;
    }
  };

  // Add work summary
  const addWorkSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      const company_id = getCompanyId();
      const employee_id = await getEmployeeid();
      if (!newWorkSummary.project_name || !newWorkSummary.start_date || !newWorkSummary.work_description) {
        showSnackbar("Please fill Project Name, Start Date, and Work Description", "error");
        return;
      }

      const response = await api.post(
        `/api/self-appraisal/${selfAppraisal.id}/work-summaries?employee_id=${employee_id}`,
        newWorkSummary,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          withCredentials:true
        }
      );
      
      setWorkSummaries(prev => [...prev, response.data.workSummary]);
      setNewWorkSummary({
        project_name: "",
        start_date: null,
        end_date: null,
        manager_name: "",
        work_description: "",
         key_highlights: "",
  challenges_faced: "",
  actions_taken: ""
      });
      
      showSnackbar("Work summary added", "success");
    } catch (error) {
      //console.error("Error adding work summary:", error);
      showSnackbar("Error adding work summary", "error");
    }
  };

  // Update work summary
  const updateWorkSummary = async (summaryId, updates) => {
    try {
      const token = localStorage.getItem('token');
      const company_id = getCompanyId();
      const employee_id = await getEmployeeid();
      
      await api.put(
        `/api/self-appraisal/${selfAppraisal.id}/work-summaries/${summaryId}?employee_id=${employee_id}`,
        updates,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          withCredentials:true
        }
      );
      
      setWorkSummaries(prev => prev.map(summary => 
        summary.id === summaryId ? { ...summary, ...updates } : summary
      ));
      
      showSnackbar("Work summary updated", "success");
    } catch (error) {
      //console.error("Error updating work summary:", error);
      showSnackbar("Error updating work summary", "error");
    }
  };

  // Delete work summary
  const deleteWorkSummary = async (summaryId) => {
    try {
      const token = localStorage.getItem('token');
      const company_id = getCompanyId();
      const employee_id = await getEmployeeid();
      await api.delete(
        `/api/self-appraisal/${selfAppraisal.id}/work-summaries/${summaryId}?employee_id=${employee_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          withCredentials:true 
        }
      );
      
      setWorkSummaries(prev => prev.filter(summary => summary.id !== summaryId));
      showSnackbar("Work summary deleted", "success");
    } catch (error) {
      //console.error("Error deleting work summary:", error);
      showSnackbar("Error deleting work summary", "error");
    }
  };

  // Save overall summary
  const saveOverallSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      const company_id = getCompanyId();
      const employee_id= await getEmployeeid();
      await api.put(
        `/api/self-appraisal/${selfAppraisal.id}/overall-summary?employee_id=${employee_id}`,
        { overall_summary: overallSummary,},{
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          withCredentials:true
        }
      );
      
      showSnackbar("Overall summary saved", "success");
      return true;
    } catch (error) {
      //console.error("Error saving overall summary:", error);
      showSnackbar("Error saving overall summary", "error");
      return false;
    }
  };

  // Submit self-appraisal
  const submitSelfAppraisal = async () => {
    try {
      const token = localStorage.getItem('token');
      setLoading(true);
      const company_id = getCompanyId();
      const employee_id= await getEmployeeid();
      const response = await api.post(
        `/api/self-appraisal/${selfAppraisal.id}/submit?employee_id=${employee_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          withCredentials:true
        }
      );
      
      setSelfAppraisal(response.data.selfAppraisal);
      showSnackbar("Self-appraisal submitted successfully!", "success");
      
      // Refresh after 2 seconds
      setTimeout(() => {
        checkEligibility();
      }, 2000);
      
    } catch (error) {
      //console.error("Error submitting self-appraisal:", error);
      showSnackbar(error.response?.data?.message || "Error submitting", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRatingChange = (ratingId, field, value) => {
    setRatings(prev => prev.map(rating => 
      rating.id === ratingId ? { ...rating, [field]: value } : rating
    ));
  };

  // Step 0: Eligibility Check
  const renderStep0 = () => (
    <Card elevation={3} sx={{ p: 4, textAlign: "center" }}>
      <Box sx={{ mb: 4 }}>
        <Assignment sx={{ fontSize: 60, color: eligibility?.eligible ? "#4caf50" : "#f44336", mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          {eligibility?.eligible ? "Self-Appraisal Window is OPEN!" : "Self-Appraisal Not Available"}
        </Typography>
        
        {eligibility?.eligible ? (
          <>
            <Typography variant="body1" color="text.secondary" paragraph>
              You can now submit your self-appraisal for the period:
            </Typography>
            <Chip 
              label={`${new Date(eligibility.cycle.period_start).toLocaleDateString()} - ${new Date(eligibility.cycle.period_end).toLocaleDateString()}`}
              icon={<DateRange />}
              sx={{ mb: 2 }}
            />
            
            <Box sx={{ my: 3, p: 2, bgcolor: "#e8f5e9", borderRadius: 2 }}>
              <Typography variant="subtitle2" color="#2e7d32">
                <AccessTime sx={{ verticalAlign: "middle", mr: 1 }} />
                Window closes: {new Date(eligibility.cycle.self_appraisal_end).toLocaleDateString()}
              </Typography>
            </Box>
            
            {eligibility.existingAppraisal ? (
              <Box>
                <Typography variant="body1" paragraph>
                  You have an existing self-appraisal in <strong>{eligibility.existingAppraisal.status}</strong> status.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Visibility />}
                  onClick={() => loadSelfAppraisal(eligibility.existingAppraisal.id)}
                  sx={{ mt: 2 }}
                >
                  Continue Self-Appraisal
                </Button>
              </Box>
            ) : (
              <Box>
                {eligibility.hasGoals ? (
                  <Typography variant="body1" paragraph>
                    You have <strong>{eligibility.goalsCount}</strong> goals to evaluate for this period.
                  </Typography>
                ) : (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No goals were assigned for this period. You can describe your work achievements instead.
                  </Alert>
                )}
                <Button
                  variant="contained"
                  startIcon={<PlayArrow />}
                  onClick={startNewSelfAppraisal}
                  disabled={loading}
                  sx={{ mt: 2, bgcolor: "#1976d2" }}
                >
                  {loading ? "Starting..." : "Start Self-Appraisal"}
                </Button>
              </Box>
            )}
          </>
        ) : (
          <>
            <Typography variant="body1" color="text.secondary" paragraph>
              {eligibility?.message || "There are no active self-appraisal windows at this time."}
            </Typography>
            <Button
              variant="outlined"
              onClick={checkEligibility}
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : "Check Again"}
            </Button>
          </>
        )}
      </Box>
    </Card>
  );

  // Step 1: Rate Goals (Only if has goals)
  const renderStep1 = () => {
    if (!eligibility?.hasGoals && ratings.length === 0) {
      // Skip to step 2 if no goals
      setActiveStep(2);
      return null;
    }

    return (
      <Card elevation={3}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography variant="h6">
              Rate Your Goals ({ratings.filter(r => r.self_rating).length}/{ratings.length})
            </Typography>
          </Box>
          
          <Divider sx={{ mb: 3 }} />
          
          {ratings.map((rating, index) => (
            <Accordion key={rating.id} defaultExpanded={index === 0} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box>
                    <Typography fontWeight="medium">
                      {rating.goal?.title || `Goal ${index + 1}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Progress: {rating.goal?.progress || 0}%
                    </Typography>
                  </Box>
                  <Box>
                    {rating.self_rating ? (
                      <Chip 
                        label={`${rating.self_rating}/5`}
                        color="success"
                        size="small"
                      />
                    ) : (
                      <Chip 
                        label="Not Rated"
                        color="default"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" paragraph>
                      {rating.goal?.description || "No description"}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography gutterBottom>
                      <strong>Your Rating (1-5)</strong>
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Rating
                        name={`rating-${rating.id}`}
                        value={rating.self_rating || 0}
                        onChange={(event, newValue) => {
                          handleRatingChange(rating.id, "self_rating", newValue);
                          updateRating(rating.id, { self_rating: newValue });
                        }}
                        precision={0.5}
                        size="large"
                      />
                      <Typography variant="body2" color="text.secondary">
                        {rating.self_rating || 0}/5
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography gutterBottom>
                      <strong>Achievement Percentage</strong>
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Slider
                        value={rating.achievement_percentage || 0}
                        onChange={(event, newValue) => {
                          handleRatingChange(rating.id, "achievement_percentage", newValue);
                        }}
                        onChangeCommitted={(event, newValue) => {
                          updateRating(rating.id, { achievement_percentage: newValue });
                        }}
                        valueLabelDisplay="auto"
                        step={5}
                        marks
                        min={0}
                        max={100}
                        sx={{ width: "80%" }}
                      />
                      <Typography variant="h6" width={60}>
                        {rating.achievement_percentage || 0}%
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Comments"
                      value={rating.self_comments || ""}
                      onChange={(e) => handleRatingChange(rating.id, "self_comments", e.target.value)}
                      onBlur={() => updateRating(rating.id, { self_comments: rating.self_comments })}
                      placeholder="Describe your achievements..."
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
          
          <Box sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}>
            <Button
              variant="outlined"
              onClick={() => setActiveStep(0)}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={() => setActiveStep(2)}
            >
              Continue to Work Summaries
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Step 2: Work Summaries
  const renderStep2 = () => (
    <Card elevation={3}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Describe Your Work ({workSummaries.length} added)
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Add the projects/tasks you worked on during this appraisal period.
        </Typography>
        
        <Divider sx={{ mb: 3 }} />
        
        {/* List of existing work summaries */}
        {workSummaries.map((summary, index) => (
          <Card key={summary.id} variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box flex={1}>
                  <Typography variant="subtitle2" gutterBottom>
                    {summary.project_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {summary.work_description}
                  </Typography>
                    {summary.key_highlights && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" fontWeight="bold">
                Key Highlights:
              </Typography>
              <Typography variant="caption" display="block">
                {summary.key_highlights}
              </Typography>
            </Box>
          )}
          
          {summary.challenges_faced && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" fontWeight="bold">
                Challenges Faced:
              </Typography>
              <Typography variant="caption" display="block">
                {summary.challenges_faced}
              </Typography>
            </Box>
          )}
          
          {summary.actions_taken && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" fontWeight="bold">
                Actions Taken:
              </Typography>
              <Typography variant="caption" display="block">
                {summary.actions_taken}
              </Typography>
            </Box>
          )}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption">
                        <strong>Period:</strong> {new Date(summary.start_date).toLocaleDateString()} 
                        {summary.end_date ? ` to ${new Date(summary.end_date).toLocaleDateString()}` : ' to Present'}
                      </Typography>
                    </Grid>
                    {summary.manager_name && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption">
                          <strong>Manager:</strong> {summary.manager_name}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => deleteWorkSummary(summary.id)}
                  sx={{ ml: 1 }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            </CardContent>
          </Card>
        ))}
        
        {/* Add new work summary form */}
        <Card variant="outlined" sx={{ mt: 3, p: 2 }}>
          <Typography variant="subtitle2" gutterBottom color="primary">
            Add New Work Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Project/Assignment Name *"
                value={newWorkSummary.project_name}
                onChange={(e) => setNewWorkSummary({ ...newWorkSummary, project_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Start Date *"
                value={newWorkSummary.start_date}
                onChange={(date) => setNewWorkSummary({ ...newWorkSummary, start_date: date })}
                renderInput={(params) => <TextField {...params} fullWidth required />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <DatePicker
                label="End Date (Optional)"
                value={newWorkSummary.end_date}
                onChange={(date) => setNewWorkSummary({ ...newWorkSummary, end_date: date })}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Manager Name (Optional)"
                value={newWorkSummary.manager_name}
                onChange={(e) => setNewWorkSummary({ ...newWorkSummary, manager_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Work Description *"
                value={newWorkSummary.work_description}
                onChange={(e) => setNewWorkSummary({ ...newWorkSummary, work_description: e.target.value })}
                required
                placeholder="Briefly describe what you did, your role, and achievements..."
              />
            </Grid>
              <Grid item xs={12}>
      <TextField
        fullWidth
        multiline
        rows={2}
        label="Key Highlights / Contributions"
        value={newWorkSummary.key_highlights || ""}
        onChange={(e) => setNewWorkSummary({ ...newWorkSummary, key_highlights: e.target.value })}
        placeholder="What did you do? What value did you add?"
      />
    </Grid>
    
    <Grid item xs={12}>
      <TextField
        fullWidth
        multiline
        rows={2}
        label="Challenges / Problems Faced"
        value={newWorkSummary.challenges_faced || ""}
        onChange={(e) => setNewWorkSummary({ ...newWorkSummary, challenges_faced: e.target.value })}
        placeholder="What issues or blockers occurred?"
      />
    </Grid>
    
    <Grid item xs={12}>
      <TextField
        fullWidth
        multiline
        rows={2}
        label="Actions Taken / How It Was Resolved"
        value={newWorkSummary.actions_taken || ""}
        onChange={(e) => setNewWorkSummary({ ...newWorkSummary, actions_taken: e.target.value })}
        placeholder="What did you do to solve it?"
      />
    </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={addWorkSummary}
                disabled={!newWorkSummary.project_name || !newWorkSummary.start_date || !newWorkSummary.work_description}
              >
                Add Work Summary
              </Button>
            </Grid>
          </Grid>
        </Card>
        
        <Box sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}>
          <Button
            variant="outlined"
            onClick={() => setActiveStep(eligibility?.hasGoals ? 1 : 0)}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={() => setActiveStep(3)}
            disabled={workSummaries.length === 0}
          >
            {workSummaries.length === 0 ? "Add at least one work summary" : "Continue to Overall Summary"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  // Step 3: Overall Summary
  const renderStep3 = () => (
    <Card elevation={3}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Overall Summary
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Provide an overall summary of your performance during this period.
        </Typography>
        
        <Divider sx={{ mb: 3 }} />
        
        <TextField
          fullWidth
          multiline
          rows={6}
          label="Your Overall Summary"
          value={overallSummary}
          onChange={(e) => setOverallSummary(e.target.value)}
          placeholder="Summarize your overall performance, key contributions, and learnings from this period..."
          sx={{ mb: 3 }}
        />
        
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Summary:</strong> You have {ratings.length} goals rated and {workSummaries.length} work summaries.
            {selfAppraisal?.status === 'Submitted' && " Your appraisal has already been submitted."}
          </Typography>
        </Alert>
        
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
          <Button
            variant="outlined"
            onClick={() => setActiveStep(2)}
          >
            Back to Work Summaries
          </Button>
          
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={saveOverallSummary}
            >
              Save Draft
            </Button>
            
            {selfAppraisal?.status === 'Draft' && (
              <Button
                variant="contained"
                color="success"
                startIcon={<Send />}
                onClick={submitSelfAppraisal}
                disabled={loading || workSummaries.length === 0}
              >
                {loading ? "Submitting..." : "Submit for Manager Review"}
              </Button>
            )}
            
            {selfAppraisal?.status === 'Submitted' && (
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                disabled
              >
                Already Submitted
              </Button>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // Determine which steps to show in stepper
  const getStepsToShow = () => {
    const allSteps = ['Check Eligibility', 'Rate Goals', 'Work Summaries', 'Overall Summary'];
    if (!eligibility?.hasGoals) {
      // Remove "Rate Goals" step if no goals
      return ['Check Eligibility', 'Work Summaries', 'Overall Summary'];
    }
    return allSteps;
  };

  const stepsToShow = getStepsToShow();
  const adjustedActiveStep = eligibility?.hasGoals ? activeStep : 
                           activeStep === 1 ? 2 : // Skip goals step
                           activeStep === 2 ? 1 : // Adjust for removed step
                           activeStep === 3 ? 2 : activeStep; // Adjust final step

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, width: '100%' }}>
        {/* Header */}
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Typography variant="h4" gutterBottom fontWeight="bold">
          Self-Appraisal
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Complete your self-assessment for the current appraisal period.
          </Typography>
        </Paper>
        
        {/* Stepper */}
        {selfAppraisal && (
          <Paper elevation={2} sx={{ p: 2, mb: 4 }}>
            <Stepper activeStep={adjustedActiveStep} alternativeLabel>
              {stepsToShow.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Paper>
        )}
        
        {/* Main Content */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {activeStep === 0 && renderStep0()}
            {activeStep === 1 && renderStep1()}
            {activeStep === 2 && renderStep2()}
            {activeStep === 3 && renderStep3()}
          </Box>
        )}
        
        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert 
            severity={snackbar.severity}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  );
};

export default EmployeeSelfAppraisal;